import type { UnifiedItineraryDay, UnifiedItineraryEntry } from "./tripItineraryModel";
import {estimatedTravelMinutes,itineraryFeasibility} from "./itineraryFeasibility";

export type OptimizationMode = "balanced" | "distance" | "time";
export interface OptimizationOptions { mode: OptimizationMode; crossDays: boolean }
export interface RouteMetrics { km: number; minutes: number; lateness: number }
export interface OptimizationPreview {
  before: UnifiedItineraryDay[]; after: UnifiedItineraryDay[];
  options: OptimizationOptions; beforeMetrics: RouteMetrics; afterMetrics: RouteMetrics;
  changed: boolean; tooLarge: boolean;
}
export function hasPosition(e: UnifiedItineraryEntry): boolean {
  return typeof e.latitude === "number" && Number.isFinite(e.latitude) && Math.abs(e.latitude) <= 90 &&
    typeof e.longitude === "number" && Number.isFinite(e.longitude) && Math.abs(e.longitude) <= 180;
}
export function canOptimizeEntry(e: UnifiedItineraryEntry): boolean {
  return ["manual", "poi", "experience"].includes(e.sourceKind) && !e.bookingConfirmed &&
    (!e.endDate || e.endDate === e.date) && hasPosition(e);
}
function minute(time: string): number | null {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return null;
  const [h, m] = time.split(":").map(Number); return h * 60 + m;
}
function duration(e: UnifiedItineraryEntry): number {
  const start = minute(e.startTime), end = minute(e.endTime);
  return start === null || end === null ? 0 : end - start;
}
function distance(a: UnifiedItineraryEntry, b: UnifiedItineraryEntry): number {
  const rad = Math.PI / 180, lat = (b.latitude! - a.latitude!) * rad, lon = (b.longitude! - a.longitude!) * rad;
  const h = Math.sin(lat / 2) ** 2 + Math.cos(a.latitude! * rad) * Math.cos(b.latitude! * rad) * Math.sin(lon / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(Math.max(0, Math.min(1, h))));
}
export function routeMetrics(days: UnifiedItineraryDay[]): RouteMetrics {
  let km = 0, minutes = 0, lateness = 0;
  for (const day of days) {
    // Climate is not a stop. Unknown positions break the route: never infer that leg.
    const entries = day.entries.filter(e => e.sourceKind !== "climate");
    for (let i = 1; i < entries.length; i++) {
      const a = entries[i - 1], b = entries[i];
      if (!hasPosition(a) || !hasPosition(b)) continue;
      const leg = distance(a, b), travel = estimatedTravelMinutes(a,b) ?? 0;
      const end = minute(a.endTime || a.startTime), start = minute(b.startTime);
      const gap = end !== null && start !== null ? Math.max(0, start - end) : 0;
      km += leg; minutes += Math.max(travel, gap);
      if (end !== null && start !== null) lateness += Math.max(0, travel - (start - end));
    }
  }
  return { km, minutes, lateness };
}
export function itineraryFingerprint(days: UnifiedItineraryDay[]): string { return JSON.stringify(days); }

/** Deterministic, bounded local search. Only occupied compatible slots are exchanged.
 * Confirmed entries, durations, fixed anchors and day capacities are preserved. */
export function optimizeItinerary(days: UnifiedItineraryDay[], options: OptimizationOptions): OptimizationPreview {
  const before = structuredClone(days), after = structuredClone(days), beforeMetrics = routeMetrics(before);
  const tooLarge = days.reduce((n, d) => n + d.entries.length, 0) > 80;
  const slots = after.flatMap((day, d) => day.entries.map((entry, i) => ({ d, i, entry: structuredClone(entry) })));
  const originalDates = new Map(slots.map(s => [s.entry.id, s.entry.date]));
  const originalIssues=itineraryFeasibility(before).length;
  function score(): number {
    const m = routeMetrics(after);
    const changes = after.flatMap(d => d.entries).filter(e => originalDates.get(e.id) !== e.date).length;
    if (m.lateness > beforeMetrics.lateness + 0.001) return Infinity;
    if(itineraryFeasibility(after).length>originalIssues)return Infinity;
    return options.mode === "distance" ? m.km : options.mode === "time" ? m.minutes : m.km + m.minutes / 15 + changes * 2;
  }
  function inSlot(e: UnifiedItineraryEntry, slot: typeof slots[number]): UnifiedItineraryEntry {
    return { ...e, date: slot.entry.date, endDate: slot.entry.endDate, startTime: slot.entry.startTime, endTime: slot.entry.endTime, itineraryOrder: slot.i };
  }
  let best = score();
  if (!tooLarge) for (let pass = 0; pass < 5; pass++) {
    let improved = false;
    for (let a = 0; a < slots.length; a++) for (let b = a + 1; b < slots.length; b++) {
      const sa = slots[a], sb = slots[b], ea = after[sa.d].entries[sa.i], eb = after[sb.d].entries[sb.i];
      if (!canOptimizeEntry(ea) || !canOptimizeEntry(eb) || (!options.crossDays && sa.d !== sb.d)) continue;
      // Do not invent or discard a time or change a known duration.
      if (Boolean(ea.startTime) !== Boolean(eb.startTime) || Boolean(ea.endTime) !== Boolean(eb.endTime) || duration(ea) < 0 || duration(ea) !== duration(eb)) continue;
      after[sa.d].entries[sa.i] = inSlot(eb, sa); after[sb.d].entries[sb.i] = inSlot(ea, sb);
      const candidate = score();
      if (candidate < best - 0.000001) { best = candidate; improved = true; }
      else { after[sa.d].entries[sa.i] = ea; after[sb.d].entries[sb.i] = eb; }
    }
    if (!improved) break;
  }
  // All movable slots need explicit positions so fixed entries keep their slots on reload.
  const changed = after.some((day, d) => day.entries.some((e, i) => e.id !== before[d].entries[i].id));
  if (changed) after.forEach(day => day.entries.forEach((e, i) => { if (canOptimizeEntry(e)) e.itineraryOrder = i; }));
  return { before, after, options, beforeMetrics, afterMetrics: routeMetrics(after), changed, tooLarge };
}

export function restoreItineraryOrder(entries: UnifiedItineraryEntry[]): UnifiedItineraryEntry[] {
  const result: Array<UnifiedItineraryEntry | undefined> = Array(entries.length).fill(undefined);
  const pending: UnifiedItineraryEntry[] = [];
  for (const e of entries) {
    const i = e.itineraryOrder;
    // A later manual time edit takes precedence over the saved route order.
    if (i !== undefined && Number.isInteger(i) && i >= 0 && i < result.length && !result[i] && entries[i].startTime === e.startTime) result[i] = e;
    else pending.push(e);
  }
  let index = 0;
  return result.map(e => e ?? pending[index++]);
}
